import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import arePropsEqual from "../../utils/arePropsEqual";

const VariantBuilderSlideOutPanel = (props) => {
  const stylesheet = style.stylesheet(
    props.builder,
    props.device,
    props.zIndex
  );

  const shouldPortalToBody = props.fitContent || props.portalToBody;

  let slideOutClasses = "cf-slide-out-panel";
  if (props.fitContent) {
    slideOutClasses += " cf-slide-out-panel--fit";
  }
  if (props.hideFooter) {
    slideOutClasses += " cf-slide-out-panel--no-footer";
  }
  if (props.noBorderRadius) {
    slideOutClasses += " cf-slide-out-panel--square";
  }
  if (props.floating) {
    slideOutClasses += " cf-slide-out-panel--floating";
  }
  if (props.show) {
    slideOutClasses += " open";
  }

  let parsedTitle = () => {
    const baseTitle =
      props.title && props.title.length > 40
        ? props.title.substr(0, 40).trim() + "..."
        : props.title;

    let tempTitleDiv = document.createElement("div");
    tempTitleDiv.innerHTML = baseTitle;

    return tempTitleDiv.textContent || tempTitleDiv.innerText || "";
  };

  useEffect(() => {
    if (props.show) {
      if (props.callbackOnOpen) {
        props.callbackOnOpen();
      }

      $(".intercom-lightweight-app-launcher").hide();
      document.addEventListener("click", handleClick);
    } else {
      $(".intercom-lightweight-app-launcher").show();
    }

    return function cleanup() {
      document.removeEventListener("click", handleClick);
    };
  }, [props.show]);

  const handleClick = (event) => {
    let element_div = document.querySelector(`#element${props.element_id}`);

    if (element_div && element_div.contains(event.target)) {
      props.setShow(false);
    }
  };

  // When portaled out of the Bootstrap modal, the modal's focus trap
  // (enforceFocus) yanks focus back and blocks typing in this panel's inputs.
  // jQuery delegates focusin via a capture-phase document listener, so it can't
  // be stopped from a descendant — disable enforceFocus while open and restore
  // it (re-arming the still-visible modal) on close.
  useEffect(() => {
    if (!shouldPortalToBody || !props.show || typeof document === "undefined") {
      return undefined;
    }

    const jquery = window.$ || window.jQuery;
    const constructor =
      jquery && jquery.fn && jquery.fn.modal && jquery.fn.modal.Constructor;

    if (!(constructor && constructor.prototype)) {
      return undefined;
    }

    const originalEnforceFocus = constructor.prototype.enforceFocus;
    constructor.prototype.enforceFocus = function noopEnforceFocus() {};
    jquery(document).off("focusin.modal");
    jquery(document).off("focusin.bs.modal");

    return () => {
      if (typeof originalEnforceFocus !== "function") return;

      constructor.prototype.enforceFocus = originalEnforceFocus;
      try {
        const visibleModal = jquery(
          ".modal.in:visible, .modal.show:visible"
        ).last();
        if (visibleModal && visibleModal.length > 0) {
          originalEnforceFocus.call({ $element: visibleModal });
        }
      } catch (error) {
        // Keep teardown resilient if Bootstrap internals differ here.
      }
    };
  }, [shouldPortalToBody, props.show]);

  useEffect(() => {
    if (!props.showOverlay || !props.show || typeof document === "undefined") {
      return undefined;
    }

    document.body.dataset.cfSlideOutOverlayOpen = "true";

    return () => {
      delete document.body.dataset.cfSlideOutOverlayOpen;
    };
  }, [props.showOverlay, props.show]);

  const panelZIndex = props.zIndex || 3;
  const overlayZIndex = props.overlayZIndex || panelZIndex - 1;

  const handleBackdropMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleBackdropClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // Defer close so the click does not fall through to the settings modal
    // underneath once this overlay unmounts.
    window.setTimeout(() => props.setShow(false), 0);
  };

  const handlePanelPointerEvent = (event) => {
    event.stopPropagation();
  };

  const panel = (
    <React.Fragment>
      {props.showOverlay && props.show && (
        <div
          className="cf-slide-out-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: overlayZIndex,
            backgroundColor: "rgba(0, 0, 0, 0.4)",
          }}
          onMouseDown={handleBackdropMouseDown}
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}
      <div
        id={`cf-slide-out-panel-${props.object_id}`}
        data-setting_name={props.setting_name}
        className={slideOutClasses}
        style={{
          ...(props.width ? { width: `${props.width}px` } : {}),
          ...(props.noBorderRadius ? { borderRadius: 0 } : {}),
          ...(shouldPortalToBody ? { zIndex: panelZIndex } : {}),
        }}
        onMouseDown={handlePanelPointerEvent}
        onClick={handlePanelPointerEvent}
      >
        <div className="cf-slide-out-header">
          <button
            type="button"
            className="close dark:text-white"
            aria-label="Close"
            onClick={() => props.setShow(false)}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
            >
              <path
                d="M12.2996 4.1001L4.09961 12.3001M12.2996 12.3001L4.09961 4.1001"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div className="cf-slide-out-header-content">
            <div className="cf-title">{parsedTitle()}</div>
            <div className="cf-description">{props.description}</div>
          </div>
        </div>

        <div className="row cf-slide-out-content">{props.children}</div>

        {!props.hideFooter && (
          <div className="row cf-slide-out-footer">
            <div className="col-xs-offset-9">
              <button
                onClick={() => props.setShow(false)}
                className={`btn blue-button field-title btn-block btn-lg`}
                style={{
                  color: "#fff",
                  border: "1px solid #00b592",
                  background: "#00b592",
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        <style dangerouslySetInnerHTML={{ __html: stylesheet }} />
      </div>
    </React.Fragment>
  );

  // Portal to body so position:fixed is relative to the viewport (escaping any
  // transformed builder ancestor) — the panel then stays put while the page
  // scrolls, positioned purely by the CSS top below.
  if (shouldPortalToBody && typeof document !== "undefined") {
    return createPortal(panel, document.body);
  }

  return panel;
};

const style = {
  stylesheet: (builder, device, zIndex) => {
    // prettier-ignore
    const desktopStyles =
    `    .cf-slide-out-backdrop {
      position: fixed;
      inset: 0;
      background-color: rgba(0, 0, 0, 0.4);
    }

    .cf-slide-out-panel {
      height: 100%;
      padding: 20px 0px;
      background-color: #fff;
      position: fixed;
      z-index: ${zIndex || 3};
      top: 0;
      right: 0;
      width: 515px;
      transform: translateX(100%);
      border-top-left-radius: 16px;
      border-bottom-left-radius: 16px;
    }

    .cf-slide-out-panel--square {
      border-radius: 0;
    }

    /* Match #options-panel top offset (59px), side/bottom inset, and shadow */
    .cf-slide-out-panel--floating {
      top: 59px;
      right: 10px;
      bottom: 10px;
      margin-top: 0;
      height: auto;
      border-radius: 0.75rem;
      overflow: hidden;
      /* Same as Tailwind shadow on #options-panel */
      box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    }

    .cf-slide-out-panel--no-footer {
      display: flex;
      flex-direction: column;
      padding-top: 0;
      padding-bottom: 0;
    }

    .cf-slide-out-panel--no-footer .cf-slide-out-content {
      flex: 1 1 auto;
      min-height: 0;
      height: auto;
      padding: 20px 24px;
      border-bottom: none;
      overscroll-behavior: contain;
    }

    /* Actions side panel: align to the settings modal top, scroll content inside */
    .cf-slide-out-panel--fit {
      height: auto;
      max-height: 750px;
      top: 11rem;
      display: flex;
      flex-direction: column;
    }

    .cf-slide-out-panel .row {
      margin-right: unset !important;
      margin-left: unset !important;
    }

    .cf-slide-out-panel .cf-slide-out-header {
      border-bottom: 1px solid #EBF1F9;
      padding: 15px;
    }

    .cf-slide-out-panel .cf-slide-out-header .close {
      float: right;
      opacity: 1;
      cursor: pointer;
    }

    .cf-slide-out-panel .cf-slide-out-header-content {
      padding-right: 24px;
      font-size: 16px;
    }

    .cf-slide-out-panel .cf-slide-out-header .cf-title {
      font-size: 16px;
      font-weight: 700;
    }

    .cf-slide-out-panel .cf-slide-out-header .cf-description {
      font-size: 14px;
      font-weight: 500;
      color: #6E818F;
    }

    .cf-slide-out-panel .cf-slide-out-content {
      height: 85%;
      overflow-y: auto;
      overflow-x: hidden;
      border-bottom: 1px solid #EBF1F9;
    }

    .cf-slide-out-panel--fit .cf-slide-out-content {
      flex: 1 1 auto;
      min-height: 0;
      height: auto;
      border-bottom: none;
      padding: 20px 24px;
      overscroll-behavior: contain;
      /* Scroll shadows: a soft fade/shadow shows at top/bottom only while there
         is more content to scroll in that direction (pure CSS, no JS). */
      background:
        linear-gradient(#fff 30%, rgba(255, 255, 255, 0)) center top,
        linear-gradient(rgba(255, 255, 255, 0), #fff 70%) center bottom,
        radial-gradient(farthest-side at 50% 0, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center top,
        radial-gradient(farthest-side at 50% 100%, rgba(0, 0, 0, 0.12), rgba(0, 0, 0, 0)) center bottom;
      background-repeat: no-repeat;
      background-size: 100% 32px, 100% 32px, 100% 12px, 100% 12px;
      background-attachment: local, local, scroll, scroll;
    }

    .cf-slide-out-panel .tab-content {
      padding: 20px 23px;
    }

    .cf-slide-out-panel .cf-slide-out-footer {
      padding: 20px 36px;
    }

    .intercom-lightweight-app {
      z-index: 1;
    }

    .cf-slide-out-panel.open {
      transform: translateX(0);
      box-shadow: 0px 4px 19px 0px #0000001A;
    }

    .cf-slide-out-panel.cf-slide-out-panel--floating.open {
      box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1);
    }
    `;

    // prettier-ignore
    const mobileStyles =
    `/* mobile styles */`;

    if (builder !== true) {
      return desktopStyles + "@media (max-width: 599px) {" + mobileStyles + "}";
    } else {
      if (device == "desktop") {
        return desktopStyles;
      } else {
        return desktopStyles + mobileStyles;
      }
    }
  },
};

export default React.memo(VariantBuilderSlideOutPanel, arePropsEqual);
